import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Incrementality-Experimente (Welle 3).

/** Gate-Namen aus `experiment_eligibility` (Migration 20260726210000).
 * `approval` und `mde_unknown` sind neu: `approval` trennt "Freigabe fehlt" von
 * "Feed zu klein" (der Feed hatte 10.756 fertige Produkte und las trotzdem
 * "zu klein"), `mde_unknown` ist der ehrliche Fall ohne genug Verlaufsdaten. */
export type ExperimentGate =
  | 'running' | 'mc_access' | 'approval' | 'population' | 'clicks' | 'mde_unknown' | 'mde';

export interface ExperimentEligibility {
  /** Freigegeben (approved oder NULL), optimiert, noch nicht hochgeladen. */
  population_count: number;
  /** Alles ausser rejected: die POTENZIELLE Population nach voller Freigabe. */
  completed_not_uploaded: number;
  /** Davon auf pending_review, also das was noch Workbench-Freigabe braucht. */
  pending_approval: number;
  pre_impressions: number;
  pre_clicks: number;
  pooled_ctr: number | null;
  /** Nachweisgrenze fuer die AKTUELL freigegebene Population. */
  mde_rel_pct: number | null;
  /** Nachweisgrenze nach VOLLSTAENDIGER Freigabe. Traegt die Entscheidung, ob
   * sich das Freigeben ueberhaupt lohnt. */
  mde_potential_pct: number | null;
  n_pairs_potential: number | null;
  /** Fenster, aus dem die MDE geschaetzt wurde (zwei 28-Tage-Bloecke). */
  mde_window_start: string | null;
  mde_window_end: string | null;
  running_experiment_id: string | null;
  eligible: boolean;
  /** Wichtigstes scheiterndes Gate. Bleibt fuer bestehende Aufrufer erhalten. */
  failing_gate: ExperimentGate | null;
  /** ALLE scheiternden Gates. Ein Nutzer mit drei offenen Punkten soll nicht
   * dreimal nacheinander gegen dieselbe Wand laufen. */
  failing_gates: ExperimentGate[];
}

export interface FeedExperiment {
  id: string;
  status: 'running' | 'completed' | 'cancelled';
  started_at: string;
  planned_end_at: string;
  ended_at: string | null;
  treatment_count: number;
  control_count: number;
  mde_rel_pct: number | null;
}

// Primaermetrik ist seit Migration 20260726160000 Klicks pro Produkt (gepaarter
// Schaetzer, CUPED-adjustiert). CTR und Impressionen bleiben Sekundaerdiagnostik:
// Google meldet Impressionen pro Produkt erst oberhalb einer Meldeschwelle,
// Klicks dagegen immer.
export interface ExperimentResults {
  status: string;
  started_at: string; planned_end_at: string; ended_at: string | null;
  /** Referenz auf den vorab festgelegten Analyseplan ("v<version>:<sha-prefix>").
   * NULL fuer Experimente, die vor der Pre-Registration gestartet sind. */
  preregistration_ref: string | null;
  window_start: string; window_end: string; window_days: number;
  /** Kalendertage im Fenster, an denen ueberhaupt Performance-Daten vorliegen.
   * Weicht von window_days ab, wenn ein Performance-Pull ausgefallen ist. */
  window_days_with_data: number;
  // Drei Zaehlungen, die sich leicht verwechseln lassen:
  //   n_treatment / n_control                    -> die ZUTEILUNG, aus dem Schaetzer-Kern
  //   n_treatment_present / n_control_present    -> davon noch existierende Produkte
  //   n_treatment_assigned / n_control_assigned  -> feed_experiments.treatment_count/control_count
  // Attrition ist die Differenz zwischen assigned und present. Seit Migration
  // 20260726230000 haengt feed_experiment_products NICHT mehr per Fremdschluessel
  // an products: der Sync loescht 30 bis 41 Prozent der Produkte pro 28 Tage, und
  // eine Cascade haette beim gepaarten Schaetzer das ganze Paar aus der Auswertung
  // geworfen. Die Zuteilungszeile ueberlebt jetzt, das geloeschte Produkt geht mit
  // 0 Klicks in die Rechnung ein (Intention-to-treat).
  n_treatment: number; n_control: number; n_pairs: number;
  n_treatment_present: number; n_control_present: number;
  n_treatment_assigned: number; n_control_assigned: number;
  attrition_treatment_pct: number | null; attrition_control_pct: number | null;
  // Sample Ratio Mismatch, Chi-Quadrat gegen das geplante Armverhaeltnis.
  // srm_passed ist Konjunkt von gates_passed.
  srm_chi2: number | null; srm_passed: boolean;
  /** Anteil der Testgruppe, der seit dem Start tatsaechlich bei Google
   * angekommen ist. Unter 95 Prozent reisst das Gate: ein zu 30 Prozent
   * gescheiterter Upload liest sich sonst als Nullergebnis. */
  delivery_rate_pct: number | null;
  // Peeking-Sperre, zweite Stufe: waehrend status = 'running' liefert die RPC fuer
  // diese acht Felder NULL. Es sind Arm-Groessen, und wer sie taeglich sieht,
  // rechnet sich den Lift selbst aus. Genau dieses Leck schliesst Abschnitt 8 der
  // Pre-Registration. Armblind und deshalb weiter sichtbar: alle n_*, alle
  // attrition_*, srm_*, delivery_rate_pct, window_* und pooled_*.
  treatment_clicks: number | null; control_clicks: number | null;
  treatment_impressions: number | null; control_impressions: number | null;
  treatment_clicks_per_product: number | null; control_clicks_per_product: number | null;
  treatment_ctr_pct: number | null; control_ctr_pct: number | null;
  treatment_day_coverage: number; control_day_coverage: number;
  theta: number | null;
  // Effektgroessen sind NULL solange status = 'running' (results_available = false).
  // Die Sperre sitzt in der RPC, nicht hier: die Funktion ist fuer authenticated
  // direkt aufrufbar, ein verstecktes UI-Element schuetzt Alpha nicht.
  lift_abs_cpp: number | null; lift_rel_pct: number | null; z_score: number | null;
  ci_rel_lower_pct: number | null; ci_rel_upper_pct: number | null;
  gates_passed: boolean; is_significant: boolean | null; results_available: boolean;
  /** G1-Basis: Klicks beider Arme zusammen. Armblind konstruiert, deshalb auch
   * waehrend der Laufzeit sichtbar. Ein Guardrail, der den Armunterschied zeigt,
   * waere ein verstecktes Peeking-Fenster. */
  pooled_clicks: number;
  pooled_clicks_per_product: number;
  /** G4 zum Auswertungszeitpunkt: `merchant_centers.access_status = 'active'`.
   * False heisst, dass am Konto etwas haengt, und dann ist jede Zahl Makulatur. */
  mc_access_active: boolean;
  /** G3 differentiell: |Attrition Test - Attrition Kontrolle| in Prozentpunkten.
   * Ueber 1,0 pp heisst, dass der Schwund mit der Behandlung zusammenhaengt. */
  attrition_diff_pp: number | null;
  attrition_diff_passed: boolean;
  /** Ergebnis ist ein Hinweis, kein Nachweis (Abschnitt 8.1 / 9 der
   * Pre-Registration). Muss sichtbar sein, sonst wird es als Nachweis gelesen. */
  result_exploratory: boolean;
  /** Maschinenlesbare Gruende, z.B. `attrition_total_gt_20`,
   * `window_days_missing_gt_3`. Unbekannte Codes zeigt die UI im Klartext an. */
  exploratory_reasons: string[];
}

export function useFeedExperiment(feedId: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const eligibilityQuery = useQuery({
    queryKey: ['experiment-eligibility', feedId],
    enabled: !!feedId,
    queryFn: async (): Promise<ExperimentEligibility | null> => {
      const { data, error } = await supabase.rpc('experiment_eligibility', { p_feed_id: feedId });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as ExperimentEligibility) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Auto-Upload-Schalter des Feed-Eigners. Traegt genau EINE Aussage in der
  // ExperimentCard: wer den automatischen Upload anhat, hat keine
  // Vergleichsgruppe mehr, weil optimierte Produkte sofort bei Google landen
  // (`upload_status = 'uploaded'`, und die Testpopulation verlangt
  // `not_uploaded`). Das ist kein Fehler, sondern die Kehrseite der Gabel — und
  // ohne diesen Satz liest sich das Population-Gate als "Feed zu klein".
  //
  // Eigener Key '…-enabled': useJourney cached unter ['profile-auto-upload',
  // user.id] das ROW-OBJEKT derselben Abfrage. React Query teilt den Cache pro
  // Key — mit demselben Key gewann der Objekt-Shape den Erst-Fetch (Layout
  // mountet useJourney vor dem Seiteninhalt) und `autoUploadQuery.data ?? false`
  // hielt ein IMMER truthy Objekt, auch bei {false, false}: die Karte
  // behauptete dann faelschlich, Auto-Upload sei eingeschaltet.
  const autoUploadQuery = useQuery({
    queryKey: ['profile-auto-upload-enabled', user?.id ?? null],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('auto_upload_new_enabled, auto_upload_drift_enabled')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      // Beide Spalten fehlen im generierten Supabase-Typ bis zum naechsten
      // Typegen (gleiche Lage wie in Schedules.tsx, das dieselben Felder
      // schreibt). Der Umweg ueber `unknown` haelt den Typfehler von der
      // Zaehlung fern, ohne die Abfrage zu veraendern.
      const row = data as unknown as
        { auto_upload_new_enabled?: boolean | null; auto_upload_drift_enabled?: boolean | null } | null;
      return !!(row?.auto_upload_new_enabled || row?.auto_upload_drift_enabled);
    },
  });

  const latestQuery = useQuery({
    queryKey: ['feed-experiment-latest', feedId],
    enabled: !!feedId,
    queryFn: async (): Promise<FeedExperiment | null> => {
      const { data, error } = await supabase
        .from('feed_experiments')
        .select('id, status, started_at, planned_end_at, ended_at, treatment_count, control_count, mde_rel_pct')
        .eq('feed_id', feedId)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return ((data?.[0] as FeedExperiment) ?? null);
    },
  });

  const experimentId = latestQuery.data && latestQuery.data.status !== 'cancelled' ? latestQuery.data.id : null;

  const resultsQuery = useQuery({
    queryKey: ['experiment-results', experimentId],
    enabled: !!experimentId && !!user,
    queryFn: async (): Promise<ExperimentResults | null> => {
      const { data, error } = await supabase.rpc('get_experiment_results', {
        p_experiment_id: experimentId, p_user_id: user!.id,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return row ?? null;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['experiment-eligibility', feedId] });
    queryClient.invalidateQueries({ queryKey: ['feed-experiment-latest', feedId] });
    queryClient.invalidateQueries({ queryKey: ['experiment-results'] });
  };

  const startMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('start-feed-experiment', {
        body: { feed_id: feedId },
      });
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('complete_feed_experiment', { p_experiment_id: id });
      if (error) throw error;
    },
    onSettled: invalidate,
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('cancel_feed_experiment', { p_experiment_id: id });
      if (error) throw error;
    },
    onSettled: invalidate,
  });

  return {
    eligibility: eligibilityQuery.data ?? null,
    eligibilityLoading: eligibilityQuery.isLoading,
    /** Mindestens einer der beiden Auto-Upload-Schalter des Nutzers ist an. */
    autoUploadEnabled: autoUploadQuery.data ?? false,
    latestExperiment: latestQuery.data ?? null,
    results: resultsQuery.data ?? null,
    startExperiment: startMutation.mutateAsync,
    isStarting: startMutation.isPending,
    completeExperiment: completeMutation.mutateAsync,
    cancelExperiment: cancelMutation.mutateAsync,
  };
}
